import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, Clock, ArrowLeft, ChevronRight } from 'lucide-react';
import { format, addDays, isSameDay, getDay, isToday } from 'date-fns';
import { cn, formatTime12Hour, getUserTimezoneAbbr } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface TimeSlot {
  id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface AvailableSlot {
  date: Date;
  start_time: string;
  end_time: string;
  timeSlotId: string;
}

interface MentorScheduleViewerProps {
  mentorId: string;
  onTimeSlotSelect: (slot: AvailableSlot) => void;
  selectedSlot?: AvailableSlot | null;
}

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

type BookingStep = 'date' | 'time';

const MentorScheduleViewer: React.FC<MentorScheduleViewerProps> = ({
  mentorId,
  onTimeSlotSelect,
  selectedSlot
}) => {
  const [currentStep, setCurrentStep] = useState<BookingStep>('date');
  const [availability, setAvailability] = useState<TimeSlot[]>([]);
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [datesWithAvailability, setDatesWithAvailability] = useState<Date[]>([]);
  const [loading, setLoading] = useState(true);
  const [generatingSlots, setGeneratingSlots] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchAvailability();
  }, [mentorId]);

  useEffect(() => {
    if (availability.length > 0) {
      generateDatesWithAvailability();
      if (selectedDate) {
        generateAvailableSlots();
      }
    }
  }, [availability, selectedDate]);

  const fetchAvailability = async () => {
    try {
      const { data, error } = await supabase
        .from('mentor_availability')
        .select('*')
        .eq('mentor_id', mentorId)
        .eq('is_active', true)
        .order('day_of_week', { ascending: true })
        .order('start_time', { ascending: true });

      if (error) throw error;
      setAvailability(data || []);
    } catch (error: any) {
      console.error('Error fetching availability:', error);
      toast({
        title: 'Error',
        description: 'Failed to load mentor availability',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const generateAvailableSlots = async () => {
    if (!selectedDate) return;

    setGeneratingSlots(true);
    try {
      // Get existing sessions for the selected date to exclude booked times
      const { data: sessions, error } = await supabase
        .from('sessions')
        .select('scheduled_date, scheduled_start_time, scheduled_end_time')
        .eq('mentor_id', mentorId)
        .eq('scheduled_date', format(selectedDate, 'yyyy-MM-dd'))
        .in('status', ['scheduled', 'in_progress']);

      if (error) throw error;

      const dayOfWeek = getDay(selectedDate);
      const dayAvailability = availability.filter(slot => slot.day_of_week === dayOfWeek);
      
      const slots: AvailableSlot[] = [];
      const now = new Date();
      const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

      dayAvailability.forEach(timeSlot => {
        // For simplicity, create hourly slots
        const startHour = parseInt(timeSlot.start_time.split(':')[0]);
        const endHour = parseInt(timeSlot.end_time.split(':')[0]);

        for (let hour = startHour; hour < endHour; hour++) {
          const startTime = `${hour.toString().padStart(2, '0')}:00`;
          const endTime = `${(hour + 1).toString().padStart(2, '0')}:00`;

          // Check if this time slot is not already booked
          const isBooked = sessions?.some(session => 
            session.scheduled_start_time === startTime
          );

          // Create slot datetime to check 24-hour advance requirement
          const slotDateTime = new Date(selectedDate);
          slotDateTime.setHours(hour, 0, 0, 0);
          
          // Filter out slots within 24 hours from now
          if (slotDateTime <= twentyFourHoursFromNow) {
            return; // Skip slots within 24 hours
          }

          if (!isBooked) {
            slots.push({
              date: selectedDate,
              start_time: startTime,
              end_time: endTime,
              timeSlotId: timeSlot.id
            });
          }
        }
      });

      setAvailableSlots(slots);
    } catch (error: any) {
      console.error('Error generating available slots:', error);
      toast({
        title: 'Error',
        description: 'Failed to load available time slots',
        variant: 'destructive',
      });
    } finally {
      setGeneratingSlots(false);
    }
  };

  const generateDatesWithAvailability = () => {
    const dates: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Generate next 30 days
    for (let i = 0; i < 30; i++) {
      const date = addDays(today, i);
      const dayOfWeek = getDay(date);
      
      // Check if mentor has availability on this day
      const hasAvailability = availability.some(slot => slot.day_of_week === dayOfWeek);
      
      if (hasAvailability) {
        dates.push(date);
      }
    }
    
    setDatesWithAvailability(dates);
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setCurrentStep('time');
    }
  };

  const handleSlotSelect = (slot: AvailableSlot) => {
    onTimeSlotSelect(slot);
  };

  const goBackToDateSelection = () => {
    setCurrentStep('date');
    setSelectedDate(undefined);
    onTimeSlotSelect(null as any);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center space-x-2">
            <Clock className="w-5 h-5 animate-spin" />
            <span>Loading schedule...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if there's any availability at all
  const hasAnyAvailability = datesWithAvailability.length > 0;

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" />
            <div>
              <div>{currentStep === 'date' ? 'Select a Date' : 'Select a Time'}</div>
              <p className="text-xs font-normal text-muted-foreground mt-0.5">
                1-hour sessions • {getUserTimezoneAbbr()}
              </p>
            </div>
          </div>
          {currentStep === 'time' && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={goBackToDateSelection}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-hidden">
        {!hasAnyAvailability ? (
          <div className="text-center py-12 px-4">
            <CalendarIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="font-medium text-lg mb-2">No Availability</h3>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              This mentor doesn't have any available time slots in the next 30 days. 
              Please check back later or contact the mentor directly.
            </p>
          </div>
        ) : (
          <>
            {currentStep === 'date' && (
              <div className="space-y-4 animate-in fade-in duration-300">
                <p className="text-sm text-muted-foreground text-center px-4">
                  Choose an available date to see time slots
                </p>
                <div className="flex justify-center overflow-x-auto pb-2">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const isInPast = date < today;
                      const hasAvailability = datesWithAvailability.some(availableDate => 
                        isSameDay(date, availableDate)
                      );
                      return isInPast || !hasAvailability;
                    }}
                    initialFocus
                    className="pointer-events-auto border rounded-lg scale-95 sm:scale-100"
                    modifiers={{
                      available: datesWithAvailability
                    }}
                    modifiersStyles={{
                      available: {
                        backgroundColor: 'hsl(var(--primary))',
                        color: 'hsl(var(--primary-foreground))',
                        fontWeight: 'bold'
                      }
                    }}
                  />
                </div>
                <div className="text-center">
                  <Badge variant="outline" className="text-xs">
                    <span className="inline-block w-2 h-2 rounded-full bg-primary mr-1.5"></span>
                    Highlighted dates have availability
                  </Badge>
                </div>
              </div>
            )}

            {currentStep === 'time' && selectedDate && (
              <div className="space-y-6 animate-in fade-in slide-in-from-right-5 duration-300">
                <div className="text-center px-4">
                  <h3 className="font-medium text-lg mb-2">
                    {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {isToday(selectedDate) ? 'Available times today' : 'Select your preferred time slot'}
                  </p>
                </div>

                {generatingSlots ? (
                  <div className="flex items-center justify-center py-12">
                    <Clock className="w-8 h-8 animate-spin text-primary" />
                    <span className="ml-3 text-muted-foreground">Loading available times...</span>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">
                      {isToday(selectedDate) 
                        ? 'No more time slots available today' 
                        : 'No available time slots for this date'}
                    </p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Try selecting a different date
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={goBackToDateSelection}
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Choose Different Date
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 px-2 sm:px-0">
                      {availableSlots.map((slot, index) => {
                        const isSelected = selectedSlot && 
                          isSameDay(slot.date, selectedSlot.date) &&
                          slot.start_time === selectedSlot.start_time;

                        return (
                          <Button
                            key={index}
                            variant={isSelected ? "default" : "outline"}
                            size="lg"
                            onClick={() => handleSlotSelect(slot)}
                            className={cn(
                              "h-auto min-h-[3.5rem] py-3 px-4 text-sm sm:text-base flex items-center justify-center",
                              "transition-all duration-200 hover:scale-105 active:scale-95",
                              isSelected && "ring-2 ring-primary ring-offset-2"
                            )}
                          >
                            <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
                            <span className="whitespace-nowrap font-medium">
                              {formatTime12Hour(slot.start_time)}
                            </span>
                            <ChevronRight className="w-3 h-3 mx-1 flex-shrink-0" />
                            <span className="whitespace-nowrap font-medium">
                              {formatTime12Hour(slot.end_time)}
                            </span>
                          </Button>
                        );
                      })}
                    </div>
                    
                    {selectedSlot && (
                      <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg text-center animate-in fade-in duration-200">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                          <h4 className="font-medium">Selected Time Slot</h4>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {format(selectedSlot.date, 'EEEE, MMMM d')} • {formatTime12Hour(selectedSlot.start_time)} - {formatTime12Hour(selectedSlot.end_time)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          1-hour session in {getUserTimezoneAbbr()}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MentorScheduleViewer;